import { Input } from '@cibli/shared';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  CountryCallingCode,
  getCallingCodeForLocale,
  SUPPORTED_COUNTRY_CALLING_CODES,
} from '@/features/register/constants/country-calling-codes';
import { useBooth } from '@/features/settings/hooks/useBooth';

interface PhoneInputProps {
  /** Full phone value with calling code, e.g. "+33612345678" ('' when empty) */
  value: string;
  /** Emits the full phone (callingCode + digits) and whether the national number is complete */
  onChange: (phone: string, isComplete: boolean) => void;
  /** Show the "composez le ..." hint when the field is empty */
  showDialHint?: boolean;
  /** Reduced height for dialogs / narrow layouts (kiosk full-page stays large by default) */
  compact?: boolean;
  autoFocus?: boolean;
}

export default function PhoneInput({
  value,
  onChange,
  showDialHint = false,
  compact = false,
  autoFocus = false,
}: PhoneInputProps) {
  const { t, i18n } = useTranslation();
  const { booth } = useBooth();

  // Determine initial country based on booth default locale or current i18n language
  const initialCountry = useMemo(() => {
    const localeToUse = booth?.defaultLocaleId || i18n.language;
    return getCallingCodeForLocale(localeToUse);
  }, [booth?.defaultLocaleId, i18n.language]);

  const [selectedCountry, setSelectedCountry] = useState<CountryCallingCode>(initialCountry);
  const [isCountryPickerOpen, setIsCountryPickerOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  const callingCode = selectedCountry.callingCode;

  // Extract national number from the full phone value
  const nationalNumber = value.startsWith(callingCode)
    ? value.slice(callingCode.length)
    : value.replace(/^\+?\d{1,4}/, '').replace(/^0+/, '');

  const expectedLength = selectedCountry.nationalNumberLength;
  const isComplete = nationalNumber.length === expectedLength;
  const isOverLength = nationalNumber.length > expectedLength;
  const isTooShort = nationalNumber.length > 0 && nationalNumber.length < expectedLength;

  const emit = (digits: string, country: CountryCallingCode) => {
    onChange(digits ? `${country.callingCode}${digits}` : '', digits.length === country.nationalNumberLength);
  };

  // Report completeness once on mount when the field is pre-filled, so the
  // parent can enable submit without the user retyping the number
  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) return;
    didMountRef.current = true;
    if (value) {
      onChange(value, nationalNumber.length === expectedLength);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Portal the country picker into the Radix DialogContent when mounted inside
  // one (fixed positioning under the translated content + body pointer-events
  // none while the modal is open); document.body otherwise (register screen)
  const rootRef = useRef<HTMLDivElement>(null);
  const [pickerPortalContainer, setPickerPortalContainer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPickerPortalContainer(
      rootRef.current?.closest<HTMLElement>('[data-slot="dialog-content"]') ?? document.body,
    );
  }, []);

  // Format national number display with pattern chunks or fallback to pairs
  const formatNationalDisplay = (digitsOnly: string) => {
    if (!digitsOnly) return '';
    const pattern = selectedCountry.pattern;
    if (pattern && pattern.length > 0) {
      const parts: string[] = [];
      let cursor = 0;
      for (const len of pattern) {
        if (cursor >= digitsOnly.length) break;
        parts.push(digitsOnly.slice(cursor, cursor + len));
        cursor += len;
      }
      if (cursor < digitsOnly.length) {
        parts.push(digitsOnly.slice(cursor));
      }
      return parts.join(' ');
    }

    let formatted = '';
    for (let i = 0; i < digitsOnly.length; i += 2) {
      formatted += digitsOnly.substring(i, Math.min(i + 2, digitsOnly.length));
      if (i + 2 < digitsOnly.length) formatted += ' ';
    }
    return formatted;
  };

  const handleNationalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let clean = e.target.value.replace(/\D/g, '');
    // If user starts typing with national trunk 0 (e.g. 06 in France), strip leading 0
    if (clean.startsWith('0')) {
      clean = clean.replace(/^0+/, '');
    }
    emit(clean, selectedCountry);
  };

  const handleCountrySelect = (country: CountryCallingCode) => {
    setSelectedCountry(country);
    setIsCountryPickerOpen(false);
    setCountrySearch('');
    emit(nationalNumber, country);
  };

  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return SUPPORTED_COUNTRY_CALLING_CODES;
    const query = countrySearch.toLowerCase().trim();
    return SUPPORTED_COUNTRY_CALLING_CODES.filter(
      c =>
        c.name.toLowerCase().includes(query) ||
        c.callingCode.includes(query) ||
        c.countryCode.toLowerCase().includes(query),
    );
  }, [countrySearch]);

  return (
    <div className="space-y-2" ref={rootRef}>
      {/* Structure Michelin UI : [Indicatif Pays] + [Champ Numéro] */}
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={() => setIsCountryPickerOpen(true)}
          className={`flex items-center gap-2.5 rounded-xl border-2 border-input bg-card hover:bg-accent/40 hover:border-primary transition justify-between focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            compact ? 'h-12 px-3 min-w-[96px]' : 'h-16 px-4 min-w-[110px]'
          }`}
          aria-label={t('FormScreen.Phone.changeCountry', {
            country: selectedCountry.name,
            callingCode: selectedCountry.callingCode,
          })}
        >
          <span className={`leading-none ${compact ? 'text-xl' : 'text-2xl'}`}>
            {selectedCountry.flag}
          </span>
          <span
            className={`font-bold text-foreground font-mono ${compact ? 'text-sm' : 'text-base'}`}
          >
            {selectedCountry.callingCode}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </button>

        <div className="relative flex-1">
          <Input
            size="xl"
            value={formatNationalDisplay(nationalNumber)}
            onChange={handleNationalChange}
            maxLength={expectedLength + 6}
            autoFocus={autoFocus}
            placeholder={selectedCountry.example}
            type="tel"
            className={`tracking-wider font-mono font-medium rounded-xl pr-11 ${
              compact ? 'h-12 text-base' : 'h-16 text-xl'
            } ${
              isComplete
                ? 'border-emerald-500 ring-emerald-500/20 focus-visible:ring-emerald-500'
                : isTooShort || isOverLength
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
            }`}
          />
          {isComplete && (
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold">
              <Check className="w-4 h-4" />
            </div>
          )}
          {(isTooShort || isOverLength) && (
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-destructive/10 text-destructive flex items-center justify-center font-bold">
              <X className="w-4 h-4" />
            </div>
          )}
        </div>
      </div>

      {/* Guided feedback (illectronisme) — localized via i18n */}
      {isTooShort && (
        <p className="text-xs text-destructive font-medium flex items-center gap-1.5 pt-1">
          <span>⚠️</span>
          <span>
            {t('FormScreen.Phone.tooShort', {
              count: expectedLength - nationalNumber.length,
              expected: expectedLength,
              country: selectedCountry.name,
            })}
          </span>
        </p>
      )}

      {isOverLength && (
        <p className="text-xs text-destructive font-medium flex items-center gap-1.5 pt-1">
          <span>⚠️</span>
          <span>
            {t('FormScreen.Phone.tooLong', {
              count: nationalNumber.length,
              expected: expectedLength,
            })}
          </span>
        </p>
      )}

      {nationalNumber.length === 0 && showDialHint && (
        <p className="text-xs text-muted-foreground pt-1">
          {t('FormScreen.Phone.dialHint', {
            country: selectedCountry.name,
          })}
        </p>
      )}

      {/* Modale / Tiroir Plein Écran de Sélection de Pays (Touch Friendly >44px) */}
      {pickerPortalContainer &&
        createPortal(
          <AnimatePresence>
            {isCountryPickerOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
              >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              className="bg-card w-full max-w-lg rounded-t-2xl sm:rounded-2xl border border-border shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
            >
            {/* Entête Modale */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <h4 className="text-lg font-bold text-foreground">Choisir votre pays</h4>
                <p className="text-xs text-muted-foreground">
                  Sélectionnez le pays de votre opérateur téléphonique
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsCountryPickerOpen(false)}
                className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition"
                aria-label="Fermer la sélection"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Barre de Recherche */}
            <div className="p-3 border-b border-border bg-muted/30">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Rechercher un pays ou un indicatif..."
                  value={countrySearch}
                  onChange={e => setCountrySearch(e.target.value)}
                  className="pl-10 h-12 text-sm bg-card"
                  autoFocus
                />
              </div>
            </div>

            {/* Liste des Pays avec Cibles Tactiles Larges (60px) */}
            <div className="overflow-y-auto p-2 space-y-1 divide-y divide-border/20">
              {filteredCountries.map(country => {
                const isCurrent = country.countryCode === selectedCountry.countryCode;
                return (
                  <button
                    key={country.countryCode}
                    type="button"
                    onClick={() => handleCountrySelect(country)}
                    className={`w-full flex items-center justify-between p-3.5 rounded-xl transition text-left min-h-[56px] ${
                      isCurrent
                        ? 'bg-primary/10 border border-primary/30 text-primary font-bold'
                        : 'hover:bg-muted/60 text-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{country.flag}</span>
                      <div>
                        <span className="text-sm font-semibold block">{country.name}</span>
                        <span className="text-xs text-muted-foreground">
                          Exemple : {country.example}
                        </span>
                      </div>
                    </div>
                    <span className="font-mono text-sm font-bold bg-muted px-2 py-1 rounded-md">
                      {country.callingCode}
                    </span>
                  </button>
                );
              })}
            </div>
              </motion.div>
            </motion.div>
            )}
          </AnimatePresence>,
          pickerPortalContainer,
        )}
    </div>
  );
}
